import { Button, Divider, Drawer, IconButton, Table, TableBody, TableCell, TableHead, TableRow, tableCellClasses } from '@mui/material';
import CardHeaderBar from '../CardHeaderBar/CardHeaderBar';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditButton from '../EditButton/EditButton';
import { FC, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { deleteTag, fetchTags } from '../../store/tagStore/tagThunk';
import { ITag, TAG_APPLIES_TO_OPTIONS } from '../../utils/tag.types';
import { ApiStatus } from '../../utils/api.types';
import { PermissionsEnum } from "../../utils/auth.types";
import DialogTagForm from '../DialogTagForm/DialogTagForm';
import ConfirmDialog from '../Dialog/ConfirmDialog';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: 13,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: 'none',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#fbfbfb',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: '#e9ecf1',
  },
  '&:last-child td, &:last-child th': {
  },
}));

/**
 * Render the `appliesTo` array as readable labels. An empty array means the
 * tag is generic, which the master shows as "All".
 */
const appliesToLabel = (appliesTo?: string[]) => {
  if (!appliesTo || appliesTo.length === 0) {
    return 'All';
  }

  return appliesTo
    .map(value => TAG_APPLIES_TO_OPTIONS.find(option => option.value === value)?.label || value)
    .join(', ');
};

/**
 * Tags master: account-scoped labels reusable on invoices and inventory items.
 */
const TagsMaster: FC = () => {
  const dispatch = useAppDispatch();

  const { tags, status } = useAppSelector(state => state.tags);
  const { permissions } = useAppSelector(state => state.auth);

  const [open, setOpen] = useState(false);
  const [tagId, setTagId] = useState('');
  const [fetchData, setFetchData] = useState('');
  const [tagToDelete, setTagToDelete] = useState<ITag | null>(null);

  const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

  const openTagForm = (id: string) => {
    setTagId(id);
    setOpen(true);
  };

  const closeDialog = (isSaved: boolean) => {
    setOpen(false);

    if (isSaved) {
      setFetchData(Math.random().toString());
    }
  };

  /**
   * Fetch the whole master (no `appliesTo` filter) whenever a save lands.
   */
  useEffect(() => {
    dispatch(fetchTags(undefined));
  }, [dispatch, fetchData]);

  const handleEditClick = useCallback((tag: ITag) => {
    openTagForm(tag._id);
  }, []);

  /**
   * Soft delete: the tag is retired to `inactive` so anything already tagged
   * keeps its reference. It can be brought back by editing the status.
   */
  const confirmDeleteTag = useCallback(async () => {
    if (!tagToDelete) {
      return;
    }

    const resData: any = await dispatch(deleteTag(tagToDelete._id));

    if (!resData.error) {
      setTagToDelete(null);
    }
  }, [dispatch, tagToDelete]);

  return (
    <div className={'c-card2'} style={{ padding: 0 }}>
      <CardHeaderBar
        title='Tags'
        cardPaddingX={0}
        cardPaddingTop={0}
        action={<EditButton size='small' label='Add Tag' icon={<AddOutlinedIcon />} onClick={() => { openTagForm('') }} />}
      />

      {tags && tags.length > 0 ?
        <div style={{ marginBottom: 12, marginTop: 5 }}>
          <Table stickyHeader sx={{ width: '100%' }} size='small' aria-label="tags table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={200}>Tag Name</StyledTableCell>
                <StyledTableCell width={200}>Applies To</StyledTableCell>
                <StyledTableCell width={250}>Description</StyledTableCell>
                <StyledTableCell width={100}>Color</StyledTableCell>
                <StyledTableCell width={100}>Status</StyledTableCell>
                <StyledTableCell width={100}>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((e: ITag) =>
                <StyledTableRow key={e._id}>
                  <StyledTableCell>{e.name}</StyledTableCell>
                  <StyledTableCell>{appliesToLabel(e.appliesTo)}</StyledTableCell>
                  <StyledTableCell>{e.description || '-'}</StyledTableCell>
                  <StyledTableCell>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: e.color || '#7f8c9b',
                        border: '1px solid #e9ecf1',
                        verticalAlign: 'middle',
                      }}
                      title={e.color || ''}
                    />
                  </StyledTableCell>
                  <StyledTableCell style={{ textTransform: 'capitalize', color: e.status === 'active' ? '#292d35' : '#9da7b7' }}>{e.status}</StyledTableCell>
                  <StyledTableCell align='left'>
                    {canUpdateAccount &&
                      <IconButton
                        onClick={() => handleEditClick(e)}
                        style={{ color: '#488dff', backgroundColor: '#fff', height: 25, width: 25 }}
                        size='small' ><EditIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    }
                    {canUpdateAccount && e.status === 'active' &&
                      <IconButton
                        onClick={() => setTagToDelete(e)}
                        style={{ color: '#c33b3b', backgroundColor: '#fff', height: 25, width: 25, marginLeft: 10 }}
                        size='small' ><DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    }
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </div> :
        <div style={{ height: 200 }}>
          <Divider sx={{ mt: 0.5, mb: 1 }} />
          No Data</div>
      }

      <Drawer
        anchor="right"
        open={open}
      >
        {<DialogTagForm open={open} tagId={tagId} onClose={closeDialog} />}
      </Drawer>

      <ConfirmDialog
        open={!!tagToDelete}
        title="Delete Tag"
        subtitle="Records already using this tag are not affected"
        message={<>Are you sure you want to delete the tag <b>{tagToDelete?.name}</b>?</>}
        warningTitle="What happens next"
        warningText="This tag will no longer be offered when tagging invoices or items. Invoices and items already tagged with it keep the tag, and you can bring it back any time by setting its status to Active."
        confirmLabel="Delete Tag"
        loading={status === ApiStatus.LOADING}
        onConfirm={confirmDeleteTag}
        onClose={() => setTagToDelete(null)}
      />
    </div>
  );
};

export default TagsMaster;
